import type { ReactNode } from 'react';
import type { DestinationEvent } from '@eventbrite/consumer-search.event-renderer';
import { defineMessages } from 'react-intl';
import { EmptyState, EventsBucket, EventsShowMore } from '../_shared';
import type { AuthState } from '../_shared/EventsBucket/EventsBucket';
import type { EventsDisplayMode } from '../_shared/EventsViewToggle/EventsViewToggle';
import { useUpcomingEvents } from './useUpcomingEvents';

const messages = defineMessages({
  emptyTitle: {
    id: 'Nothing planned right now',
    defaultMessage: 'Nothing planned right now',
  },
  emptySubtitle: {
    id: 'Follow this organizer to stay in the loop for future plans',
    defaultMessage: 'Follow this organizer to stay in the loop for future plans',
  },
});

interface Props {
  organizerId: string;
  eventbriteBaseUrl: string;
  initialEvents: DestinationEvent[];
  initialHasMore: boolean;
  initialTotal: number;
  locale: string;
  eventsDisplayMode: EventsDisplayMode;
  selectedDateFilter?: string | null;
  includeStarted?: boolean;
  mobileCalendarControl?: ReactNode;
  authState: AuthState;
}

export default function UpcomingEvents(props: Props) {
  const { events, isLoading, isError, fetchNextPage, hasMorePages, isFetchingNextPage } =
    useUpcomingEvents(
      props.organizerId,
      props.initialEvents,
      props.initialHasMore,
      props.initialTotal,
      props.selectedDateFilter,
      props.includeStarted,
    );

  if (isError) {
    return null;
  }

  if (!isLoading && events.length === 0) {
    return (
      <div data-testid="organizer-upcoming">
        {props.mobileCalendarControl}
        <EmptyState
          organizerId={props.organizerId}
          eventbriteBaseUrl={props.eventbriteBaseUrl}
          title={messages.emptyTitle}
          subtitle={messages.emptySubtitle}
        />
      </div>
    );
  }

  return (
    <div data-testid="organizer-upcoming">
      <EventsBucket
        locale={props.locale}
        events={events}
        loadingState={{ isFeLoading: isLoading }}
        bucketName={'upcoming-events'}
        organizerId={props.organizerId}
        variant={props.eventsDisplayMode}
        groupByDay={Boolean(props.selectedDateFilter)}
        mobileHeaderAccessory={props.mobileCalendarControl}
        authState={props.authState}
        showSaveAction
        showShareAction
      />
      {hasMorePages && !isLoading && (
        <EventsShowMore onShowMore={() => fetchNextPage()} isLoadingMore={isFetchingNextPage} />
      )}
    </div>
  );
}
